import { getOrganizationUsers } from "@/api/endpoints/organization";
import { useCanManageUsers } from "@/hooks/useCanManageUsers";
import { ROUTES } from "@/routes_new";
import { Button } from "@components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import ErrorComponent from "@components/ui/error";
import { Skeleton } from "@components/ui/skeleton";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { generatePath, Link, Route, useParams } from "react-router-dom";
import Heading from "../Heading";
import UserSheet from "./partials/UserSheet";
import UsersTable from "./partials/UsersTable";
import DeleteUser from "./partials/DeleteUser";
import InviteUser from "./partials/InviteUser";
import AllEarsTitle from "@components/AllEarsTitle";

export default function SettingsUsersView() {
  const { t } = useLingui();

  const title = t`Användare`;

  // URL state
  const { orgUuid } = useParams<{ orgUuid: string }>();

  // Server state
  const canManageUsers = useCanManageUsers();

  const {
    data: usersData,
    error: usersError,
    isLoading: isUsersLoading,
  } = useQuery({
    queryKey: [orgUuid, "users"],
    queryFn: () => getOrganizationUsers(orgUuid),
    enabled: !!orgUuid,
  });

  return (
    <>
      <AllEarsTitle title={title} />

      <Route
        path={ROUTES.SETTINGS_TEAM_INVITE_USER_ORGID}
        component={InviteUser}
      />
      <Route
        exact
        path={ROUTES.SETTINGS_TEAM_USER_ORGID}
        component={UserSheet}
      />
      <Route
        path={ROUTES.SETTINGS_TEAM_DELETE_USER_ORGID}
        component={DeleteUser}
      />

      <Heading
        heading={title}
        description={t`Bjud in kollegor till All Ears och hantera deras åtkomst`}
      />

      <div className="w-full p-4 lg:p-6 max-w-7xl flex flex-col gap-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">
              <Trans>Användare</Trans>
            </CardTitle>
            <CardAction>
              <Button asChild>
                <Link
                  to={generatePath(ROUTES.SETTINGS_TEAM_INVITE_USER_ORGID, {
                    orgUuid,
                  })}
                >
                  <Trans>Bjud in kollega</Trans>
                  <PlusIcon />
                </Link>
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent>
            {usersData ? (
              usersData.ok && Array.isArray(usersData.data) ? (
                <UsersTable
                  users={usersData.data}
                  orgUuid={orgUuid}
                  canManageUsers={canManageUsers}
                />
              ) : (
                <ErrorComponent
                  heading={t`Åh nej!`}
                  description={t`Något gick fel när vi försökte ladda in användare.`}
                />
              )
            ) : isUsersLoading ? (
              <Skeleton className="h-32" />
            ) : usersError ? (
              <ErrorComponent
                heading={t`Åh nej!`}
                description={t`Något gick fel när vi försökte ladda in användare.`}
                error={usersError}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
